import { currentFt3dSceneWindow } from '../ft3d-hud/scene-frame';
// throw-object.ts — the parent-window verbs behind Drop / Pick up / Throw (owner 2026-09-15,
// plans/ft3d-throw-object-design-2026-09-15.md). Runs in the campaign page (object-card bundle), where the
// caster cache, the active caster, the FT3D placement lane (window.aoeManager) and the inventory move lane
// (window.move_draggable_inventory) all live. The FT3D iframe reaches these through the radial (scene-radial.ts)
// and the object card reaches them through its action buttons (render-object-card.ts).
//
// Drop  = the object lands at the actor's feet (0 AP, the XZ widget's drop_inventory, never surfaced in FT3D).
// Pick up = floor → the actor (1 AP in combat; the endpoint reach-gates loose floor rows and needs a free hand).
// Throw = ONE generic ability (Throw Object) placed like any lobbed AOE: a synthetic button carries this
//         throw's numeric range (Body × size × weight — the JS twin of the server maths) and the inventory id
//         as use_ability_variable_2; FT3DAOEPlacement.cast() sends both with the landing point.
//         Only what the actor carries: in hand = a throw (1 AP in combat); carried but not in hand (a bag) = DRAWN to a
//         free hand and thrown (2 AP in combat — ⚡⚡ on the aim readout; owner 2026-09-16, the bag radial).
//         The rules are janus-engine/actions/throw-object-gate.ts — this file only binds them to the page caches.

import { resolveCachedItemForRender } from './find-cached-item';
import { encounterField, hasCampaignPermission, inventoryField, itemsCol, layerOf, posCoord, sizeIdOf } from '../shared/array-types';
import { THROW_OBJECT_ABILITY_ID, THROW_OBJECT_VARIABLE } from '../shared/throw-object-range';
import { pageTelekineticReach, pageThrowObjectMaxSizeId } from '../shared/object-handling-browser';
import { NON_THROWABLE_ITEM_TYPES, THROW_OBJECT_DRAW_AP, creatureHasFreeHand, throwObjectVerdict, throwObjectRangeRefusal, type ThrowObjectGateReaders, type ThrowObjectMode } from '../janus-engine/actions/throw-object-gate';
import { selectThrowableWeapon, throwableProviderMetadata } from '../janus-engine/display/throwable-selector';
import { readInventoryTemplateId } from '../janus-engine/compile/item-field-reader';
import type { ProviderCatalogRows } from '../janus-engine/compile/provider-catalog-order';

const SPHERE_AOE_TYPE_ID = '981032B8-4F0B-43C9-8D98-67C693E4DB42';
const gateReaders: ThrowObjectGateReaders = {
    value: (row, key) => row ? layerOf(row, key) : null,
    inventoryField: (row, key) => row ? inventoryField(row, key) : null,
    itemField: (row, key) => row ? itemsCol(row, key) : null,
    creatureSizeId: creature => sizeIdOf(creature),
    itemSizeId: row => inventoryField(row, 'inventory_size') || layerOf(row, 'size'),
    throwMaxSizeId: creature => pageThrowObjectMaxSizeId(creature), // Gravity Sovereign (2026-10-07)
    telekineticReach: (creature, row) => pageTelekineticReach(creature, row), // DOS2-style telekinesis from where it lies (2026-10-07)
    resolveInventory: id => resolveCachedItemForRender(id) as Row | null,
};

type Row = Record<string, unknown>;
type Host = Window & {
    ftGetActiveCasterId?: () => string;
    ftGetActiveEncounterId?: () => string;
    FateTide?: { casterCache?: Map<string, Row>; encounterCache?: Map<string, Row> };
    aoeManager?: { startFt3d?: (button: HTMLElement) => void; ft3dActive?: unknown };
    move_draggable_inventory?: (...args: unknown[]) => Promise<void>;
    displayWarning?: (message: string) => void;
};
const host = window as Host;

function num(value: unknown): number {
    const v = Array.isArray(value) ? value[0] : value;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}
function activeCaster(): { id: string; row: Row } | null {
    const id = String(host.ftGetActiveCasterId?.() ?? '').trim();
    const row = id ? host.FateTide?.casterCache?.get(id) ?? host.FateTide?.casterCache?.get(id.toUpperCase()) : undefined;
    return id && row ? { id, row } : null;
}
/** The free-hand rule as THIS viewer meets it: a campaign GM moves rows past it (the move endpoint's GM/editor bypass). */
function viewerFreeHand(casterRow: Row): boolean | null {
    return hasCampaignPermission(casterRow) ? true : creatureHasFreeHand(casterRow);
}
function encounterId(): string {
    return String(host.ftGetActiveEncounterId?.() ?? '').trim();
}
/** in_combat is an ENCOUNTER column (never encounter_creatures) — the cached encounter, else the caster's nested copy. */
function encounterInCombat(casterRow: Row): boolean {
    const id = encounterId();
    const cache = host.FateTide?.encounterCache;
    const enc = (id && (cache?.get(id) ?? cache?.get(id.toUpperCase()))) || casterRow.encounter_arr;
    return num(encounterField(enc, 'in_combat')) === 1;
}
/** Is this row anywhere in the caster's own inventory tree (hands, pack, a pouch inside the pack)? Twin of the
 *  server's possessor climb (ft_throw_object_is_held_by), walked over the cached tree with a hop cap. */
export function casterHoldsRow(casterRow: Row, inventoryId: string): boolean {
    const wanted = inventoryId.toUpperCase();
    const seen = new Set<unknown>();
    const walk = (entries: unknown, depth: number): boolean => {
        if (!Array.isArray(entries) || depth > 8) return false;
        for (const entry of entries) {
            if (!entry || typeof entry !== 'object' || seen.has(entry)) continue;
            seen.add(entry);
            const row = entry as Row;
            if (String(inventoryField(row, 'inventory_id') ?? '').toUpperCase() === wanted) return true;
            if (walk(row.inventory_in_container, depth + 1)) return true;
        }
        return false;
    };
    return walk(casterRow.inventory, 0);
}
/** The actor's own entry for a row, anywhere in its inventory tree. */
function findCarriedEntry(casterRow: Row, inventoryId: string): Row | null {
    const wanted = inventoryId.toUpperCase();
    const walk = (entries: unknown, depth: number): Row | null => {
        if (!Array.isArray(entries) || depth > 8) return null;
        for (const entry of entries) {
            if (!entry || typeof entry !== 'object') continue;
            const row = entry as Row;
            if (String(inventoryField(row, 'inventory_id') ?? '').toUpperCase() === wanted) return row;
            const nested = walk(row.inventory_in_container, depth + 1);
            if (nested) return nested;
        }
        return null;
    };
    return walk(casterRow.inventory, 0);
}
export function ftCasterHoldsRow(casterId: string, inventoryId: string): boolean {
    const row = host.FateTide?.casterCache?.get(casterId) ?? host.FateTide?.casterCache?.get(casterId.toUpperCase());
    return !!row && casterHoldsRow(row, inventoryId);
}

export interface ThrowObjectPlan {
    ok: boolean;
    reason: string | null;
    casterId: string;
    inventoryId: string;
    name: string;
    /** 'throw' = already in hand · 'draw' = carried but not in hand: drawn to a free hand, then thrown (owner 2026-09-16) ·
     *  'telekinesis' = an unattended floor row hurled from where it lies (object_interaction_range_ft, 2026-10-07; 1 AP in combat). */
    mode: ThrowObjectMode;
    /** AP the throw spends: 0 out of combat, 1 from the hand, 1 + THROW_OBJECT_DRAW_AP drawn from the bag. */
    apCost: number;
    inCombat: boolean;
    weightLb: number;
    capacityLb: number;
    rangeFt: number | null;
    twoHanded: boolean;
    held: boolean;
    onFloor: boolean;
    /** Pick up needs a free hand (the somatic rule); null when the hand counts are unreadable. */
    freeHand: boolean | null;
    sizeSteps: number | null;
    throwerSizeId: string;
    objectSizeId: string;
}

/** What this actor could do with this row — janus-engine's throw gate over the page caches (Janus is the authority). */
export function throwObjectPlan(inventoryId: string): ThrowObjectPlan {
    const caster = activeCaster();
    const row = resolveCachedItemForRender(inventoryId) as Row | null;
    const base: ThrowObjectPlan = { ok: false, reason: null, casterId: caster?.id ?? '', inventoryId, name: '', mode: 'draw', apCost: 0, inCombat: false, weightLb: 0, capacityLb: 0, rangeFt: null, twoHanded: false, held: false, onFloor: false, freeHand: null, sizeSteps: null, throwerSizeId: '', objectSizeId: '' };
    if (!caster) return { ...base, reason: 'Control a character first' };
    if (!row) return { ...base, reason: 'That object is not loaded yet' };
    const name = String(inventoryField(row, 'inventory_name') ?? itemsCol(row, 'item_name') ?? 'object');
    const verdict = throwObjectVerdict({ caster: caster.row, casterId: caster.id, row, encounterId: encounterId(),
        encounter: host.FateTide?.encounterCache?.get(encounterId().toUpperCase()) }, gateReaders);
    const inCombat = encounterInCombat(caster.row);
    const apCost = inCombat ? 1 + (verdict.mode === 'draw' ? THROW_OBJECT_DRAW_AP : 0) : 0;
    // PHP's AP half of the gate (a draw proves its whole budget up front); a GM keeps the same AP economy.
    const aps = num((caster.row.encounter_creatures as Row | undefined)?.aps);
    const apRefusal = verdict.refusal === null && apCost > 1 && aps < apCost
        ? `Drawing that and throwing it takes ${apCost} AP — you have ${aps}.` : null;
    return {
        ...base, name, mode: verdict.mode, apCost, inCombat, weightLb: verdict.weightLb, capacityLb: verdict.capacityLb, rangeFt: verdict.rangeFt,
        // A telekinetic throw (Gravity Sovereign) keeps the one-size-larger half range but takes no hands: never labelled "both hands".
        twoHanded: verdict.twoHanded && !verdict.telekinetic, held: verdict.held, onFloor: verdict.onFloor, freeHand: viewerFreeHand(caster.row),
        sizeSteps: verdict.sizeSteps, throwerSizeId: verdict.throwerSizeId, objectSizeId: verdict.objectSizeId,
        ok: verdict.refusal === null && apRefusal === null && verdict.rangeFt !== null,
        reason: verdict.refusal ?? apRefusal ?? throwObjectRangeRefusal(verdict),
    };
}

/** Arm the landing-point placement for a throw; the commit sends variable_1/2 with the point. */
export function ftThrowObjectStart(inventoryId: string): boolean {
    const plan = throwObjectPlan(inventoryId);
    const row = resolveCachedItemForRender(inventoryId) as Row | null;
    if (!plan.ok || plan.rangeFt === null) {
        host.displayWarning?.(plan.reason ?? 'Cannot throw that.');
        return false;
    }
    if (!host.aoeManager?.startFt3d) {
        host.displayWarning?.('Throwing needs the scene placement lane.');
        return false;
    }
    const button = document.createElement('button');
    Object.assign(button.dataset, {
        ability_id: THROW_OBJECT_ABILITY_ID,
        ftCasterId: plan.casterId,
        item_type: 'quick_ability',
        bsOriginalTitle: `${plan.mode === 'draw' ? 'Draw & throw' : plan.mode === 'telekinesis' ? 'Telekinesis:' : 'Throw'} ${plan.name} (${plan.rangeFt} ft)`,
        ftActionPointCost: String(plan.apCost),
        range: String(plan.rangeFt),
        aoe_type: SPHERE_AOE_TYPE_ID,
        aoe_distance: '5',
        aoe_distance_height: '5',
        arcing_trajectory: '1',
        arcing_flat: '1', // the flattest 45° arc (apex = distance / 4): a hand throw clears a normal ceiling
        ftUseAbilityVariable1: THROW_OBJECT_VARIABLE,
        ftUseAbilityVariable2: inventoryId,
        ftThrownObjectSizeId: plan.objectSizeId ?? '',
        // Telekinesis: the flight leaves from the prop where it lies — the scene measures range, arc and line of effect from it.
        ftThrowLaunchEntityId: plan.mode === 'telekinesis' ? inventoryId.toUpperCase() : '',
        ftThrowCompositionId: String(layerOf(row ?? {}, 'item_composition') ?? ''),
    });
    host.aoeManager.startFt3d(button);
    return true;
}

/** Drop at the actor's feet: the FT3D scene's drop_inventory move, addressed to the actor's own position. */
export function ftDropInventoryAtFeet(inventoryId: string): boolean {
    const caster = activeCaster();
    const row = resolveCachedItemForRender(inventoryId) as Row | null;
    const encounter = encounterId();
    if (!caster || !row || !encounter || typeof host.move_draggable_inventory !== 'function') {
        host.displayWarning?.(caster ? 'Nowhere to drop that here.' : 'Control a character first');
        return false;
    }
    const qty = Math.max(1, num(inventoryField(row, 'item_qty')) || 1);
    const x = posCoord(caster.row, 'x_pos') ?? 0;
    const y = posCoord(caster.row, 'y_pos') ?? 0;
    const z = posCoord(caster.row, 'z_pos') ?? 0;
    // Every row is (x, depth, elevation): the ground pair as (x_pos, z_pos) + ft3d_elevation, on or off the scene page
    // (the retired XZ widget's (x, z)-plane post wrote the caster's elevation into the depth column on a shaped encounter).
    void host.move_draggable_inventory(inventoryId, qty, caster.id, encounter, 'drop_inventory', 1,
        x, y, 0, '', 0, z);
    return true;
}

/** Floor → the actor's on-hand inventory (1 AP in combat; the endpoint reach-gates loose floor rows and needs a free hand). */
export function ftPickUpInventory(inventoryId: string): boolean {
    const caster = activeCaster();
    const row = resolveCachedItemForRender(inventoryId) as Row | null;
    const encounter = encounterId();
    if (!caster || !row || !encounter || typeof host.move_draggable_inventory !== 'function') {
        host.displayWarning?.(caster ? 'That object is not loaded yet.' : 'Control a character first');
        return false;
    }
    if (viewerFreeHand(caster.row) === false) {
        host.displayWarning?.('Your hands are full — free a hand to pick that up.');
        return false;
    }
    const qty = Math.max(1, num(inventoryField(row, 'item_qty')) || 1);
    void host.move_draggable_inventory(inventoryId, qty, encounter, caster.id, 'move_inventory', 1).then(() => {
        // Presentation follows accepted inventory truth. The generic move lane resolves even when its
        // request was refused, so prove the row is now carried before asking the scene for a pickup cue.
        if (!ftCasterHoldsRow(caster.id, inventoryId)) return;
        currentFt3dSceneWindow()?.postMessage({
            type: 'ft3d-pickup-accepted-v1', payload: { actorId: caster.id, inventoryId: inventoryId.toUpperCase() },
        }, location.origin);
    });
    return true;
}

/** Draws in flight, per actor + row: a double activation must not post two equips. */
const drawsInFlight = new Set<string>();

/**
 * Equip a carried throwing weapon into a free hand through the real move lane, then arm its own throw (the action bar
 * only dispatches an equipped weapon). Every precondition is re-read at activation — a wheel is a snapshot — and the
 * equip is skipped when the weapon is already in hand. The move lane applies the server receipt to the caches before it
 * resolves, and the server's own refusal (size fit, hands, AP) is shown by that lane; this reports only what it saw.
 */
export async function ftDrawWeaponThenThrow(casterId: string, inventoryId: string, flight: 'direct' | 'lob'): Promise<boolean> {
    const weaponApi = (window as Window & { ftActionBarThrowWeapon?: (casterId: string, invId: string, flight?: 'direct' | 'lob') => boolean }).ftActionBarThrowWeapon;
    const warn = (message: string): false => { host.displayWarning?.(message); return false; };
    const key = `${casterId.toUpperCase()}:${inventoryId.toUpperCase()}`;
    if (drawsInFlight.has(key)) return false;
    if (typeof host.move_draggable_inventory !== 'function' || !weaponApi) return warn('Throwing needs the action bar.');
    const caster = activeCaster();
    if (!caster || caster.id.toUpperCase() !== casterId.toUpperCase()) return false; // control moved on since the wheel opened
    const inHandNow = (): boolean => { const plan = throwObjectPlan(inventoryId); return plan.held && plan.mode === 'throw'; };
    // The action bar builds the weapon attack from the COMPILED equipped entry; the equip receipt moves the row first and
    // the recompile (weapon layers from its classification) follows on the caster update.
    const compiledOnActor = (): boolean => {
        const row = activeCaster()?.row;
        const entry = row ? findCarriedEntry(row, inventoryId) : null;
        const equipped = String(inventoryField(entry ?? {}, 'equipped') ?? '').trim();
        return !!entry && equipped !== '' && equipped !== '0'
            && (num(layerOf(entry, 'range_end')) > 0 || num(layerOf(entry, 'item_throwable')) === 1);
    };
    // Any new user action during the wait (a press, a key) is a change of intent: the draw stays drawn, the throw is dropped.
    let superseded = false;
    const supersede = (): void => { superseded = true; };
    document.addEventListener('pointerdown', supersede, true);
    document.addEventListener('keydown', supersede, true);
    drawsInFlight.add(key);
    try {
        if (!inHandNow()) {
            const plan = throwObjectPlan(inventoryId);
            if (!plan.held) return warn('You are no longer carrying that.');
            if (plan.freeHand === false) return warn('Your hands are full — free a hand to draw that.');
            const aps = num((caster.row.encounter_creatures as Row | undefined)?.aps);
            if (plan.inCombat && aps < 2) return warn(`Drawing that and throwing it takes 2 AP — you have ${aps}.`);
            await host.move_draggable_inventory(inventoryId, 1, casterId, casterId, 'equip_inventory', 1);
            // A deferred receipt reaches the cache through the websocket instead: allow it a short moment.
            const deadline = Date.now() + 3000;
            while (!inHandNow() && Date.now() < deadline) await new Promise(resolve => window.setTimeout(resolve, 150));
            if (!inHandNow()) return warn('It did not come to hand, so nothing was thrown.');
        }
        const compiledBy = Date.now() + 10000;
        while (!compiledOnActor() && !superseded && Date.now() < compiledBy) await new Promise(resolve => window.setTimeout(resolve, 200));
        if (superseded) return false;
        // Re-read everything right before arming: the same actor, still controlled, the row still in its hand.
        if (String(host.ftGetActiveCasterId?.() ?? '').toUpperCase() !== casterId.toUpperCase() || !inHandNow()) return false;
        if (!compiledOnActor()) return warn('It is in your hand — use Throw again in a moment.');
        return weaponApi(casterId, inventoryId, flight) || warn('It is in your hand, but the throw could not be armed — use Throw again.');
    } catch (error) {
        console.error('FT3D weapon draw failed:', error);
        return warn('The draw failed — try again.');
    } finally {
        document.removeEventListener('pointerdown', supersede, true);
        document.removeEventListener('keydown', supersede, true);
        drawsInFlight.delete(key);
    }
}

/** Short-lived authored input snapshots, scoped to actor + inventory + template; answers are always derived locally. */
const throwableInputs = new Map<string, { templateId: string; readAt: number; inventory: Row; catalog: Record<string, Row>; metadata: ProviderCatalogRows }>();
const throwableAsks = new Map<string, Promise<boolean | null>>();
const throwableKey = (id: string, actorId: string): string => `${actorId.toUpperCase()}:${id.toUpperCase()}`;

/** Janus's rule over what the page holds: item_throwable, or a MELEE weapon with a range_end. null = cannot tell here. */
function weaponIsThrowable(item: Row, inventoryId: string): boolean | null {
    const held = selectThrowableWeapon(item);
    if (held !== null) return held;
    const cached = throwableInputs.get(throwableKey(inventoryId, String(host.ftGetActiveCasterId?.() ?? '')));
    if (!cached || Date.now() - cached.readAt > 60000 || cached.templateId !== readInventoryTemplateId(item)?.toUpperCase()
        || String(inventoryField(item, 'enchantment_template_id') ?? '') !== String(cached.inventory.enchantment_template_id ?? '')) return null;
    const definition = cached.catalog[cached.templateId];
    return definition ? selectThrowableWeapon({ ...definition, inventory: cached.inventory }, cached.catalog, cached.metadata) : null;
}

/** Acquire bounded raw authored inputs on demand, then run the browser selector. */
export function ftEnsureWeaponThrowable(inventoryId: string): Promise<boolean | null> {
    const id = inventoryId.toUpperCase();
    const item = resolveCachedItemForRender(id) as Row | null;
    if (!item) return Promise.resolve(null);
    const known = weaponIsThrowable(item, id);
    if (known !== null) return Promise.resolve(known);
    const actorId = String(host.ftGetActiveCasterId?.() ?? '').toUpperCase();
    const templateId = readInventoryTemplateId(item)?.toUpperCase();
    if (!actorId || !templateId) return Promise.resolve(null);
    const key = throwableKey(id, actorId);
    let ask = throwableAsks.get(key);
    if (!ask) {
        // Bounded: a stalled answer must never hold the wheel (Drop / Details) hostage — it opens without the weapon verbs.
        const abort = new AbortController();
        const timer = window.setTimeout(() => abort.abort(), 4000);
        ask = fetch(`${BASE_URL}options/api/inventory_throwable_inputs_inc.php`, {
            method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ inventory_id: id, caster_id: actorId }),
            signal: abort.signal,
        }).then(response => response.ok ? response.json() : null).then((json: { ok?: boolean; inputs?: { inventory_id?: string; actor_id?: string; inventory?: Row; catalog?: Record<string, Row>; provider_metadata?: unknown } } | null) => {
            const inputs = json?.inputs;
            const current = resolveCachedItemForRender(id) as Row | null;
            const metadata = throwableProviderMetadata(inputs?.provider_metadata);
            if (json?.ok !== true || inputs?.inventory_id !== id || inputs.actor_id !== actorId || !inputs.inventory || !inputs.catalog || !metadata
                || String(inputs.inventory.inventory_item_id ?? '').toUpperCase() !== templateId
                || String(host.ftGetActiveCasterId?.() ?? '').toUpperCase() !== actorId || readInventoryTemplateId(current)?.toUpperCase() !== templateId) return null;
            throwableInputs.set(key, { templateId, readAt: Date.now(), inventory: inputs.inventory, catalog: inputs.catalog, metadata });
            return current ? weaponIsThrowable(current, id) : null;
        }, () => null).finally(() => { window.clearTimeout(timer); throwableAsks.delete(key); });
        throwableAsks.set(key, ask);
    }
    return ask;
}

export type InventoryVerbAction ={ label: string; icon?: string; enabled?: boolean; reason?: string | null; run: () => void };

/** Is this row equipped in a hand on the actor (main / off / both hands / tail)? Read off the plan's gate verdict. */
function inHand(plan: ThrowObjectPlan): boolean {
    return plan.held && plan.mode === 'throw';
}

/**
 * Drop / Throw (and a throwing weapon's own Throw / Lob) for ONE row the actor carries — shared by the creature
 * radial's "Held items" and the FT3D bag radial (owner 2026-09-16). Anything not already in hand must come out
 * through a free hand: Drop needs one, Throw draws it into one and costs an extra ⚡ in combat.
 */
export function ftInventoryVerbActions(casterId: string, inventoryId: string): InventoryVerbAction[] {
    const item = resolveCachedItemForRender(inventoryId) as Row | null;
    if (!item) return [];
    const id = inventoryId.toUpperCase();
    const plan = throwObjectPlan(id);
    const type = String(itemsCol(item, 'item_type') ?? '');
    const bolts = (ap: number): string => ap > 0 ? ` · ${'⚡'.repeat(ap)}` : '';
    const handFree = inHand(plan) || plan.freeHand !== false;
    const out: InventoryVerbAction[] = [{
        label: 'Drop', icon: 'drop_inventory', enabled: handFree,
        reason: handFree ? 'Set it down at your feet' : 'Your hands are full — free a hand to take that out.',
        run: () => { ftDropInventoryAtFeet(id); },
    }];
    if (NON_THROWABLE_ITEM_TYPES.has(type)) return out;
    // A throwing WEAPON in hand throws as a weapon (its own dice, Direct by default, Lob on request — the fold-out's
    // rules); anything can still be hurled as an object (weight-ranged improvised damage).
    const weaponApi = (window as Window & { ftActionBarThrowWeapon?: (casterId: string, invId: string, flight?: 'direct' | 'lob') => boolean }).ftActionBarThrowWeapon;
    // Janus's rule (ft_throw_mode_resolve / the throw_weapon branch): a weapon flagged throwable, or one with a thrown range_end.
    const throwableWeapon = type === 'weapon' && weaponIsThrowable(item, id) === true && !!weaponApi && plan.held;
    const throwingWeapon = throwableWeapon && inHand(plan);
    if (throwingWeapon) {
        const ap = plan.inCombat ? 1 : 0;
        out.push(
            { label: 'Throw', icon: 'throw_weapon', reason: `Straight weapon throw${bolts(ap)}`, run: () => { weaponApi!(casterId, id, 'direct'); } },
            { label: 'Lob', icon: 'throw_weapon', reason: `Arc it over cover (a ceiling still blocks)${bolts(ap)}`, run: () => { weaponApi!(casterId, id, 'lob'); } },
        );
    } else if (throwableWeapon) {
        // A throwing weapon in the bag (owner 2026-09-17): the draw is a REAL equip into the free hand (the move lane's
        // 1 AP in combat), then the weapon's own throw (1 AP) — ⚡⚡ together. Cancelling the aim leaves it in hand.
        const ap = plan.inCombat ? 2 : 0;
        const aps = num((activeCaster()?.row.encounter_creatures as Row | undefined)?.aps);
        const refusal = plan.freeHand === false ? 'Your hands are full — free a hand to draw that.'
            : ap > 0 && aps < ap ? `Drawing that and throwing it takes ${ap} AP — you have ${aps}.` : null;
        const draw = (flight: 'direct' | 'lob'): void => { void ftDrawWeaponThenThrow(casterId, id, flight); };
        out.push(
            { label: 'Throw', icon: 'throw_weapon', enabled: refusal === null, reason: refusal ?? `Draw it, then a straight weapon throw${bolts(ap)}`, run: () => draw('direct') },
            { label: 'Lob', icon: 'throw_weapon', enabled: refusal === null, reason: refusal ?? `Draw it, then arc it over cover${bolts(ap)}`, run: () => draw('lob') },
        );
    }
    const how = plan.mode === 'draw' ? 'drawn to a free hand, then thrown' : plan.mode === 'telekinesis' ? 'flies from where it lies (L while aiming = straight)' : 'L while aiming = straight';
    out.push({
        label: throwableWeapon ? 'Throw as object' : 'Throw', icon: 'throw_inventory', enabled: plan.ok,
        reason: plan.ok ? `Range ${plan.rangeFt} ft · ${plan.weightLb} lb${plan.twoHanded ? ' · both hands' : ''} · ${how}${bolts(plan.apCost)}` : plan.reason,
        run: () => { ftThrowObjectStart(id); },
    });
    return out;
}

/** The scene's creature radial (controlled actor only): one Drop / Throw set per equipped row (owner 2026-09-15). */
export function ftHeldItemVerbActions(casterId: string): Array<{ label: string; icon?: string; enabled?: boolean; reason?: string | null; children?: () => InventoryVerbAction[]; run: () => void }> {
    const row = host.FateTide?.casterCache?.get(casterId) ?? host.FateTide?.casterCache?.get(casterId.toUpperCase());
    if (!row || String(host.ftGetActiveCasterId?.() ?? '').toUpperCase() !== casterId.toUpperCase()) return [];
    const out: ReturnType<typeof ftHeldItemVerbActions> = [];
    for (const entry of Array.isArray(row.inventory) ? row.inventory : []) {
        if (!entry || typeof entry !== 'object') continue;
        const item = entry as Row;
        const id = String(inventoryField(item, 'inventory_id') ?? '').toUpperCase();
        const equipped = String(inventoryField(item, 'equipped') ?? '').trim();
        if (!id || !equipped || equipped === '0') continue;
        if (NON_THROWABLE_ITEM_TYPES.has(String(itemsCol(item, 'item_type') ?? ''))) continue;
        const name = String(inventoryField(item, 'inventory_name') ?? itemsCol(item, 'item_name') ?? 'item');
        out.push({ label: name, icon: 'inventory', run: () => {}, children: () => ftInventoryVerbActions(casterId, id) });
    }
    return out;
}

Object.assign(window, { ftResolveCachedInventoryRow: resolveCachedItemForRender, ftThrowObjectPlan: throwObjectPlan, ftThrowObjectStart, ftDropInventoryAtFeet, ftPickUpInventory, ftCasterHoldsRow, ftHeldItemVerbActions, ftInventoryVerbActions, ftEnsureWeaponThrowable });
